// src/components/accounting/AccountingDashboard.js
// School accounting dashboard: local school figures composed with the
// live ledger (via Laravel GET /accounting/dashboard). Every widget
// degrades gracefully when the ledger is unreachable.
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  Skeleton, Alert, Chip, Button, LinearProgress,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { accountingAPI } from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import FeeCard from '../fees/FeeCard';
import FeeReconciliationReport from './FeeReconciliationReport';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const statusColor = (status) => {
  switch (status) {
    case 'POSTED':
    case 'PAID':
      return 'success';
    case 'PARTIALLY_PAID':
    case 'APPROVED':
      return 'warning';
    case 'VOID':
    case 'REVERSED':
      return 'error';
    default:
      return 'default';
  }
};

const formatOptionalCurrency = (value) => (value == null ? '—' : formatCurrency(value));

const classAttributionLabel = (basis) => ({
  billed_at: 'Billed placement',
  current_fallback: 'Current placement fallback',
  mixed_snapshot_and_fallback: 'Billed and legacy placement',
  multiple_placements: 'Payment attribution unresolved',
  multiple_placements_without_payments: 'Multiple billed placements',
  multiple_placements_unassigned: 'Placement allocation unavailable',
  missing_allocation: 'No matching allocation',
}[basis] || 'Placement source unavailable');

const AccountingDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [queueError, setQueueError] = useState('');
  const [queue, setQueue] = useState({ unbilled_allocations: [], unpushed_payments: [] });
  const [queueOpen, setQueueOpen] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [allocationPage, setAllocationPage] = useState(1);
  const [allocationHasMore, setAllocationHasMore] = useState(false);
  const [paymentPage, setPaymentPage] = useState(1);
  const [paymentHasMore, setPaymentHasMore] = useState(false);
  const [queueLoading, setQueueLoading] = useState(false);
  const [previewingRetry, setPreviewingRetry] = useState(false);
  const [retryPreview, setRetryPreview] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await accountingAPI.getDashboard();
      setData(response.data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load the accounting dashboard.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadQueue = useCallback(async (options = {}) => {
    const {
      historyPage: requestedHistoryPage = 1,
      allocationPage: requestedAllocationPage = 1,
      paymentPage: requestedPaymentPage = 1,
      appendHistory = false,
      appendAllocations = false,
      appendPayments = false,
    } = options;
    setQueueLoading(true);
    setQueueError('');
    try {
      const response = await accountingAPI.getSyncQueue({
        history_page: requestedHistoryPage,
        allocation_page: requestedAllocationPage,
        payment_page: requestedPaymentPage,
      });
      const queueData = response.data;
      setQueue((current) => ({
        ...queueData,
        history: appendHistory
          ? [...(current.history || []), ...(queueData.history || [])]
          : (appendAllocations || appendPayments ? current.history : queueData.history),
        unbilled_allocations: appendAllocations
          ? [...(current.unbilled_allocations || []), ...(queueData.unbilled_allocations || [])]
          : (appendHistory || appendPayments ? current.unbilled_allocations : queueData.unbilled_allocations),
        unpushed_payments: appendPayments
          ? [...(current.unpushed_payments || []), ...(queueData.unpushed_payments || [])]
          : (appendHistory || appendAllocations ? current.unpushed_payments : queueData.unpushed_payments),
      }));

      const queuePagination = queueData.queue_pagination || {};
      const isInitialLoad = !appendHistory && !appendAllocations && !appendPayments;
      if (appendHistory || isInitialLoad) {
        setHistoryPage(Number(queueData.history_pagination?.current_page || requestedHistoryPage));
        setHistoryHasMore(Boolean(queueData.history_pagination?.has_more));
      }
      if (appendAllocations || isInitialLoad) {
        setAllocationPage(Number(queuePagination.allocations?.current_page || requestedAllocationPage));
        setAllocationHasMore(Boolean(queuePagination.allocations?.has_more));
      }
      if (appendPayments || isInitialLoad) {
        setPaymentPage(Number(queuePagination.payments?.current_page || requestedPaymentPage));
        setPaymentHasMore(Boolean(queuePagination.payments?.has_more));
      }
    } catch (err) {
      setQueueError(err?.response?.data?.message || 'Failed to load the sync queue.');
    } finally {
      setQueueLoading(false);
    }
  }, []);

  const retryQueue = async () => {
    setRetrying(true);
    setRetryResult(null);
    setRetryPreview(null);
    try {
      const response = await accountingAPI.retrySyncQueue();
      setRetryResult({
        ok: true,
        partial: Number(response.data.failed_count || 0) > 0 || Boolean(response.data.has_more),
        hasMore: Boolean(response.data.has_more),
        message: response.data.message,
      });
      fetchDashboard();
      loadQueue();
    } catch (err) {
      setRetryResult({ ok: false, message: err?.response?.data?.message || 'Retry failed.' });
    } finally {
      setRetrying(false);
    }
  };

  const previewRetryQueue = async () => {
    setPreviewingRetry(true);
    setRetryPreview(null);
    try {
      const response = await accountingAPI.previewSyncQueueRetry();
      setRetryPreview({ ok: true, data: response.data });
    } catch (err) {
      setRetryPreview({ ok: false, message: err?.response?.data?.message || 'Could not preview the retry queue.' });
    } finally {
      setPreviewingRetry(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading) {
    return (
      <Box p={3}>
        <Skeleton variant="text" width={260} height={30} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'No data.'}</Alert>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchDashboard}>
          Retry
        </Button>
      </Box>
    );
  }

  const { kpis, ledger, monthly, by_class, recent_documents, cash_position_local, sync_health } = data;
  const drift = ledger.receivable !== null && Number(ledger.receivable) !== Number(kpis.outstanding);

  return (
    <Box>
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" gutterBottom>Accounting Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            School figures composed with the live ledger
            {ledger.reachable ? (
              <> · <CheckCircleIcon fontSize="inherit" sx={{ verticalAlign: 'middle' }} color="success" /> Ledger connected</>
            ) : (
              <> · <ErrorIcon fontSize="inherit" sx={{ verticalAlign: 'middle' }} color="error" /> Ledger offline - local figures only</>
            )}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchDashboard}>
          Refresh
        </Button>
      </Box>

      {ledger.note && (
        <Alert severity={ledger.reachable ? 'info' : 'warning'} sx={{ mb: 3 }}>
          {ledger.note}
        </Alert>
      )}
      {drift && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Local outstanding ({formatCurrency(kpis.outstanding)}) differs from ledger receivable ({formatCurrency(ledger.receivable)}).
          Check Sync Health below for unpushed items.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <FeeCard title="Fees Billed" value={formatCurrency(kpis.billed)} subtitle="All allocations" icon={ReceiptIcon} gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" />
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <FeeCard title="Collected" value={formatCurrency(kpis.collected)} subtitle={`${kpis.collection_rate}% collection rate`} icon={TrendingUpIcon} gradient="linear-gradient(135deg, #43a047 0%, #1b5e20 100%)" />
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <FeeCard title="Outstanding" value={formatCurrency(kpis.outstanding)} subtitle={`${kpis.active_students} active students`} icon={WarningIcon} gradient="linear-gradient(135deg, #ff9800 0%, #e65100 100%)" />
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <FeeCard title="Ledger Receivable" value={ledger.receivable !== null ? formatCurrency(ledger.receivable) : 'Offline'} subtitle="Debtor aging total" icon={AccountBalanceIcon} gradient="linear-gradient(135deg, #1e88e5 0%, #0d47a1 100%)" />
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Collections - last 6 months</Typography>
              <Bar
                data={{
                  labels: monthly.map((m) => m.label),
                  datasets: [{ label: 'Collected (UGX)', data: monthly.map((m) => m.collected), backgroundColor: '#43a047' }],
                }}
                options={{ responsive: true, plugins: { legend: { display: false } } }}
              />
              <Box mt={2}>
                <Typography variant="body2" gutterBottom>Collection rate: <b>{kpis.collection_rate}%</b></Typography>
                <LinearProgress variant="determinate" value={Math.min(kpis.collection_rate, 100)} sx={{ height: 8, borderRadius: 4 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Receivable by age (ledger)</Typography>
              {ledger.buckets ? (
                <Bar
                  data={{
                    labels: ['Current', '1-30', '31-60', '61-90', '90+'],
                    datasets: [{
                      label: 'UGX',
                      data: [ledger.buckets.current, ledger.buckets.days_1_30, ledger.buckets.days_31_60, ledger.buckets.days_61_90, ledger.buckets.over_90].map((v) => Number(v) || 0),
                      backgroundColor: ['#43a047', '#fdd835', '#fb8c00', '#e53935', '#b71c1c'],
                    }],
                  }}
                  options={{ indexAxis: 'y', responsive: true, plugins: { legend: { display: false } } }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">Ledger offline.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <FeeReconciliationReport />

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <SyncIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                Sync Health
              </Typography>
              {!sync_health.bridge_enabled && (
                <Alert severity="info" sx={{ mb: 2 }}>Accounting bridge is off - everything below is local only.</Alert>
              )}
              <Typography variant="body2">Unbilled allocations: <b>{sync_health.unbilled_allocations ?? '-'}</b></Typography>
              <Typography variant="body2">Unpushed payments: <b>{sync_health.unpushed_payments ?? '-'}</b></Typography>
              <Typography variant="body2" color={(sync_health.failed_allocations || sync_health.failed_payments) ? 'error.main' : 'text.secondary'}>
                Failed allocations / payments: <b>{sync_health.failed_allocations ?? 0} / {sync_health.failed_payments ?? 0}</b>
              </Typography>
              <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                <Button size="small" variant="outlined" onClick={() => { setQueueOpen((o) => !o); if (!queueOpen) loadQueue(); }}>
                  {queueOpen ? 'Hide queue' : 'View queue'}
                </Button>
                <Button size="small" variant="outlined" disabled={previewingRetry} onClick={previewRetryQueue}>
                  {previewingRetry ? 'Previewing...' : 'Preview retry'}
                </Button>
                <Button size="small" variant="contained" disabled={retrying} onClick={retryQueue}>
                  {retrying ? 'Retrying...' : 'Retry batch'}
                </Button>
              </Box>
              {retryPreview?.ok && (
                <Alert severity={retryPreview.data.can_retry ? 'info' : 'warning'} sx={{ mt: 2 }}>
                  {retryPreview.data.can_retry
                    ? `Read-only preview: next batch will try up to ${retryPreview.data.next_batch.invoice_groups} invoice group(s) and ${retryPreview.data.next_batch.receipts} receipt(s). After one batch, about ${retryPreview.data.remaining_after_batch.invoice_groups} invoice group(s) and ${retryPreview.data.remaining_after_batch.receipts} receipt(s) will remain. ${retryPreview.data.ledger_checks}`
                    : `Retry is not ready. ${retryPreview.data.bridge_enabled ? '' : 'Accounting bridge is disabled. '}${retryPreview.data.missing_prerequisites?.length ? `Missing: ${retryPreview.data.missing_prerequisites.join(', ')}.` : ''}`}
                </Alert>
              )}
              {retryPreview && !retryPreview.ok && <Alert severity="error" sx={{ mt: 2 }}>{retryPreview.message}</Alert>}
              {retryResult && (
                <Alert severity={!retryResult.ok ? 'error' : (retryResult.partial ? 'warning' : 'success')} sx={{ mt: 2 }}>{retryResult.message}</Alert>
              )}
              {queueOpen && (
                <Box mt={2}>
                  {queueError && <Alert severity="error" sx={{ mb: 2 }}>{queueError}</Alert>}
                  {(allocationHasMore || paymentHasMore) && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      The queue is paginated and sorts least-attempted sources first. Retry processes up to 100 invoice groups and 100 receipts per batch; run another batch while items remain.
                    </Alert>
                  )}
                  {[...queue.unbilled_allocations, ...queue.unpushed_payments].length === 0 && (
                    <Typography variant="body2" color="text.secondary">Queue is empty - everything is mirrored.</Typography>
                  )}
                  {queue.unbilled_allocations.map((q) => (
                    <Typography key={`a-${q.id}`} variant="caption" display="block">
                      Source: {q.source_ref || 'not attempted'} &middot; Last attempt: {q.last_attempt_at || 'never'}<br />
                      BILL · {q.student} · {q.detail}{q.term ? ` · ${q.term}` : ''} · {formatCurrency(q.amount)} · {q.date} · attempts: {q.attempts}{q.error ? ` · ${q.error}` : ''}
                    </Typography>
                  ))}
                  {allocationHasMore && (
                    <Button size="small" sx={{ mt: 1 }} disabled={queueLoading} onClick={() => loadQueue({ allocationPage: allocationPage + 1, appendAllocations: true })}>
                      {queueLoading ? 'Loading...' : 'Load more unbilled allocations'}
                    </Button>
                  )}
                  {queue.unpushed_payments.map((q) => (
                    <Typography key={`p-${q.id}`} variant="caption" display="block">
                      Source: {q.source_ref || 'not attempted'} &middot; Last attempt: {q.last_attempt_at || 'never'}<br />
                      RECEIPT · {q.student} · {q.detail} · {formatCurrency(q.amount)} · {q.date} · attempts: {q.attempts}{q.error ? ` · ${q.error}` : ''}
                    </Typography>
                  ))}
                  {paymentHasMore && (
                    <Button size="small" sx={{ mt: 1 }} disabled={queueLoading} onClick={() => loadQueue({ paymentPage: paymentPage + 1, appendPayments: true })}>
                      {queueLoading ? 'Loading...' : 'Load more unpushed payments'}
                    </Button>
                  )}
                  {(queue.history || []).length > 0 && (
                    <Box mt={3}>
                      <Typography variant="subtitle2" gutterBottom>Recent sync actions</Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Source</TableCell>
                              <TableCell>Action</TableCell>
                              <TableCell>Reference</TableCell>
                              <TableCell>Outcome</TableCell>
                              <TableCell>Resolution note</TableCell>
                              <TableCell>Staff</TableCell>
                              <TableCell>Ledger document</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {queue.history.map((event, index) => (
                              <TableRow key={`${event.source_ref}-${event.created_at}-${index}`}>
                                <TableCell>{event.created_at ? new Date(event.created_at).toLocaleString() : '-'}</TableCell>
                                <TableCell>{String(event.source_type || '').toUpperCase()}</TableCell>
                                <TableCell>{event.action || 'sync'}</TableCell>
                                <TableCell>{event.source_ref}</TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={event.outcome}
                                    color={event.outcome === 'success' ? 'success' : (event.outcome === 'failed' ? 'error' : 'default')}
                                  />
                                </TableCell>
                                <TableCell>
                                  {event.resolution
                                    ? `${String(event.resolution.code || 'handling recorded').split('_').join(' ')}${event.resolution.note ? `: ${event.resolution.note}` : ''}`
                                    : '-'}
                                </TableCell>
                                <TableCell>{event.user_name || (event.user_id ? `User #${event.user_id}` : 'System / background job')}</TableCell>
                                <TableCell>{event.ledger_document_id || '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {historyHasMore && (
                        <Button size="small" sx={{ mt: 1 }} disabled={queueLoading} onClick={() => loadQueue({ historyPage: historyPage + 1, appendHistory: true })}>
                          {queueLoading ? 'Loading...' : 'Load older sync actions'}
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                {sync_health.last_failure_at
                  ? `Most recent failed sync: ${new Date(sync_health.last_failure_at).toLocaleString()}. Open the queue for recovery details.`
                  : 'No failed fee syncs are currently recorded.'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Fee balance by class / stream</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Class / stream</TableCell>
                      <TableCell align="right">Billed</TableCell>
                      <TableCell align="right">Collected</TableCell>
                      <TableCell align="right">Outstanding</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {by_class.length === 0 && (
                      <TableRow><TableCell colSpan={4} align="center">No allocations yet.</TableCell></TableRow>
                    )}
                    {by_class.map((row) => (
                      <TableRow key={row.row_key || `${row.class_stream_id || row.class_stream}-${row.attribution}`}>
                        <TableCell>
                          {row.class_stream || row.class}
                          <Typography variant="caption" color="text.secondary" display="block">
                            {classAttributionLabel(row.attribution)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{formatOptionalCurrency(row.allocated)}</TableCell>
                        <TableCell align="right">{formatOptionalCurrency(row.collected)}</TableCell>
                        <TableCell align="right"><b>{formatOptionalCurrency(row.outstanding)}</b></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                All-time local fee totals. New allocations retain their billed placement after the snapshot migrations deploy; older rows fall back to current placement. If a student and fee group span multiple placements, the report leaves the payment and balance unassigned instead of guessing.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Recent ledger documents</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Doc</TableCell>
                      <TableCell>Student</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recent_documents.length === 0 && (
                      <TableRow><TableCell colSpan={4} align="center">{ledger.reachable ? 'Nothing posted yet.' : 'Ledger offline.'}</TableCell></TableRow>
                    )}
                    {recent_documents.map((d, i) => (
                      <TableRow key={`${d.kind}-${d.number}-${i}`}>
                        <TableCell>{d.kind === 'invoice' ? 'INV' : 'RCT'} {d.number}<br /><Typography variant="caption" color="text.secondary">{d.date}</Typography></TableCell>
                        <TableCell>{d.student || '-'}</TableCell>
                        <TableCell align="right">{formatCurrency(d.amount)}</TableCell>
                        <TableCell><Chip size="small" label={d.status || '?'} color={statusColor(d.status)} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Cash position (school accounts)</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cash_position_local.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>{a.name}</TableCell>
                    <TableCell>{a.account_type}</TableCell>
                    <TableCell align="right">{formatCurrency(a.current_balance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AccountingDashboard;
